import { cn } from '@virginexperiencedays/tailwind';

import { BodyText, type BodyTextProps } from '../typography/BodyText';

import type { HTMLAttributes, ButtonHTMLAttributes } from 'react';

export enum DataTestId {
  list = 'usp-grid-icon-list',
  listItem = 'usp-grid-icon-list-item',
  listIcon = 'usp-grid-icon-list-icon',
  listTitle = 'usp-grid-icon-list-title',
  listTitleButton = 'usp-grid-icon-list-title-button',
}

// This follows Composition Pattern + Atomic Design
// Usage Reference: See implementation in storybook template mocks

/**
 * Used in Product Detail Page
 */

// element for grid list
export const UspGridList = ({ className, children, ...rest }: HTMLAttributes<HTMLUListElement>) => (
  <ul
    data-testid={DataTestId.list}
    className={cn('grid grid-cols-3 gap-x-2 gap-y-6 md:grid-cols-6', className)}
    {...rest}
  >
    {children}
  </ul>
);

// element for grid list item
export const UspGridListItem = ({
  className,
  children,
  onClick,
  ...rest
}: Omit<HTMLAttributes<HTMLLIElement>, 'onClick'> & {
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
}) => {
  if (onClick) {
    return (
      <li data-testid={DataTestId.listItem} className={cn('flex flex-col', className)} {...rest}>
        <button
          data-testid={DataTestId.listTitleButton}
          className={cn(styles.listItem, 'border-0 bg-none p-0 underline underline-offset-2')}
          onClick={onClick}
        >
          {children}
        </button>
      </li>
    );
  }

  return (
    <li data-testid={DataTestId.listItem} className={cn(styles.listItem, className)} {...rest}>
      {children}
    </li>
  );
};

// element for grid list item icon wrapper
export const UspGridListIcon = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    data-testid={DataTestId.listIcon}
    className={cn('flex items-center justify-center', className)}
    {...rest}
  >
    {children}
  </div>
);

// element for grid list item title in BodyText or button-wrapped BodyText when onClick is provided
export const UspGridListTitle = ({ className, children, ...rest }: BodyTextProps) => (
  <BodyText
    data-testid={DataTestId.listTitle}
    size="small"
    className={cn('text-balance font-semibold leading-[1.3]', className)}
    {...rest}
  >
    {children}
  </BodyText>
);

const styles = { listItem: 'flex flex-col items-center justify-center gap-4 text-center' };
